import type {
  CML,
  CmlParent,
  CMLStatus,
  Drawing,
  InspectionCampaign,
  Line,
  Measurement,
  Module,
  PIDDoc,
  Platform,
  Report,
  Site,
  Tank,
  User,
  Vessel,
} from "./types";

export type {
  CML,
  Drawing,
  InspectionCampaign,
  Line,
  Measurement,
  Module,
  PIDDoc,
  Platform,
  Report,
  Site,
  Tank,
  User,
  Vessel,
};

// ══════════════════════════════════════════════════════════════════════
// DATA SOURCES
//
// Asset hierarchy (sites + platforms): based on PUBLIC info from
//   - Valeura Energy corporate site (valeuraenergy.com)
//   - Press releases (Mar 2026 Manora drilling, Nong Yao C ramp-up,
//     Wassana FID, Jasmine infill drilling Nov 2024)
//   - Offshore Technology / Oil & Gas Journal coverage
//
// Line/Drawing/CML/Measurement/Photo detail: SIMULATED for mockup demo.
//   Real piping inventory must come from Valeura under NDA for pilot.
// ══════════════════════════════════════════════════════════════════════

// ──────────────────────────────────────────────────────────────────────
// Current user (mock — for header display)
// ──────────────────────────────────────────────────────────────────────
export const currentUser: User = {
  id: "user-001",
  email: "k.sakpolachet@valeura.com",
  name: "Khun Sakpolachet",
  role: "engineer",
  certLevel: 3,
  tenant: "Valeura Energy Thailand Ltd",
};

// ──────────────────────────────────────────────────────────────────────
// Sites — Real Valeura Thailand portfolio (4 operated licences)
// ──────────────────────────────────────────────────────────────────────
// Coordinates: field-level approximate from GEM.wiki public records.
// Platform-level precision requires AIS data / DMF records (proprietary).
export const sites: Site[] = [
  {
    id: "site-jsm",
    code: "JSM",
    name: "Jasmine + Ban Yen",
    location: "Licence B5/27 · Northern Pattani Basin · 60 m WD",
    operator: "Valeura Energy (Thailand) Ltd · 100% WI",
    installationYear: 2005,
    platforms: 6,
    cmlCount: 1284,
    criticalCount: 7,
    lastInspection: "2025-11-18",
    lat: 11.292,
    lon: 100.724,
    distanceKm: 130,
  },
  {
    id: "site-mnr",
    code: "MNR",
    name: "Manora",
    location: "Licence G1/48 · Kra Sub-basin · 45 m WD",
    operator: "Valeura Energy (Thailand) Ltd · 70% WI",
    installationYear: 2014,
    platforms: 1,
    cmlCount: 318,
    criticalCount: 1,
    lastInspection: "2026-02-04",
    lat: 11.694,
    lon: 100.571,
    distanceKm: 120,
  },
  {
    id: "site-wsn",
    code: "WSN",
    name: "Wassana",
    location: "Licence G10/48 · Pattani Basin · 48 m WD",
    operator: "Valeura Energy (Thailand) Ltd · 100% WI",
    installationYear: 2015,
    platforms: 2,
    cmlCount: 196,
    criticalCount: 0,
    lastInspection: "2025-12-05",
    lat: 7.916,
    lon: 101.33,
    distanceKm: 120,
  },
  {
    id: "site-nyo",
    code: "NYO",
    name: "Nong Yao",
    location: "Licence G11/48 · Central Pattani Basin · 75 m WD",
    operator: "Valeura Energy (Thailand) Ltd · 90% WI",
    installationYear: 2015,
    platforms: 3,
    cmlCount: 762,
    criticalCount: 4,
    lastInspection: "2026-01-22",
    lat: 7.782,
    lon: 102.12,
    distanceKm: 165,
  },
];

// ──────────────────────────────────────────────────────────────────────
// Platforms — verified from Valeura/Offshore Technology coverage
//
// Jasmine + Ban Yen (B5/27): 6 wellhead platforms + FPF-003 FPSO
//   First oil: Jasmine A (2005), B+C (2007), B2+D+Ban Yen A (2008)
// Nong Yao (G11/48): NY-A (main), NY-B (wellhead), NY-C (newest, MOPU)
// Manora (G1/48): single Manora A platform + FSO
// Wassana (G10/48): MOPU (to be replaced by CPP end-2027) + FSO
// ──────────────────────────────────────────────────────────────────────
// Platform coordinates:
// - Jasmine A center from GEM.wiki; Jasmine B (3 km NNW) & C (3.2 km SSE) from
//   Offshore Magazine 2005 article — calculated to lat/lon (verified).
// - Jasmine B2/D and Ban Yen A: estimated cluster positions (no public source).
// - Other sites: single-platform estimates around field center.
export const platforms: Platform[] = [
  // Jasmine + Ban Yen — 6 wellhead platforms + FPSO offset
  { id: "plt-jsm-a", siteId: "site-jsm", code: "JSM-A", name: "Jasmine A", type: "production",
    lat: 11.292, lon: 100.724, firstOilYear: 2005, coordConfidence: "verified" },
  { id: "plt-jsm-b", siteId: "site-jsm", code: "JSM-B", name: "Jasmine B", type: "wellhead",
    lat: 11.317, lon: 100.714, firstOilYear: 2007, coordConfidence: "verified" },
  { id: "plt-jsm-b2", siteId: "site-jsm", code: "JSM-B2", name: "Jasmine B2", type: "wellhead",
    lat: 11.323, lon: 100.722, firstOilYear: 2008, coordConfidence: "estimated" },
  { id: "plt-jsm-c", siteId: "site-jsm", code: "JSM-C", name: "Jasmine C", type: "wellhead",
    lat: 11.265, lon: 100.735, firstOilYear: 2007, coordConfidence: "verified" },
  { id: "plt-jsm-d", siteId: "site-jsm", code: "JSM-D", name: "Jasmine D", type: "wellhead",
    lat: 11.288, lon: 100.752, firstOilYear: 2008, coordConfidence: "estimated" },
  { id: "plt-byn-a", siteId: "site-jsm", code: "BYN-A", name: "Ban Yen A", type: "wellhead",
    lat: 11.255, lon: 100.770, firstOilYear: 2008, coordConfidence: "estimated" },

  // Nong Yao
  { id: "plt-nyo-a", siteId: "site-nyo", code: "NY-A", name: "Nong Yao A", type: "production",
    lat: 7.782, lon: 102.12, firstOilYear: 2015, coordConfidence: "verified" },
  { id: "plt-nyo-b", siteId: "site-nyo", code: "NY-B", name: "Nong Yao B", type: "wellhead",
    lat: 7.770, lon: 102.135, coordConfidence: "estimated" },
  { id: "plt-nyo-c", siteId: "site-nyo", code: "NY-C", name: "Nong Yao C (MOPU)", type: "production",
    lat: 7.795, lon: 102.105, firstOilYear: 2024, coordConfidence: "estimated" },

  // Manora
  { id: "plt-mnr-a", siteId: "site-mnr", code: "MNR-A", name: "Manora A", type: "production",
    lat: 11.694, lon: 100.571, firstOilYear: 2014, coordConfidence: "verified" },

  // Wassana
  { id: "plt-wsn-m", siteId: "site-wsn", code: "WSN-M", name: "Wassana MOPU", type: "production",
    lat: 7.916, lon: 101.33, firstOilYear: 2015, coordConfidence: "verified" },
  { id: "plt-wsn-cpp", siteId: "site-wsn", code: "WSN-CPP", name: "Wassana CPP (under construction)",
    type: "processing", lat: 7.918, lon: 101.335, coordConfidence: "estimated" },
];

// ──────────────────────────────────────────────────────────────────────
// Modules on Jasmine A (focus platform in mockup)
// Note: specific deck names/elevations are SIMULATED — typical convention
// for production platforms (process deck + utilities below + cellar deck).
// Real Jasmine A configuration not public.
// ──────────────────────────────────────────────────────────────────────
export const modules: Module[] = [
  { id: "mod-jsm-a-pd", platformId: "plt-jsm-a", code: "PD", name: "Production Deck", elevation: 24.5 },
  { id: "mod-jsm-a-ud", platformId: "plt-jsm-a", code: "UD", name: "Utility Deck", elevation: 18.0 },
  { id: "mod-jsm-a-cd", platformId: "plt-jsm-a", code: "CD", name: "Cellar Deck", elevation: 12.0 },
];

// ──────────────────────────────────────────────────────────────────────
// Lines — SIMULATED for mockup
// Material specs are realistic for Gulf of Thailand offshore service.
// ──────────────────────────────────────────────────────────────────────
export const lines: Line[] = [
  {
    id: "ln-001",
    moduleId: "mod-jsm-a-pd",
    lineNo: '6"-P-1234',
    service: "Crude Oil to FPF-003",
    designCode: "ASME B31.3",
    designPressureMpa: 4.0,
    designTempC: 65,
    material: "ASTM A106 Gr.B",
    npsInch: 6,
    schedule: "80",
    insulated: true,
    insulationType: "Mineral wool 50 mm",
  },
  {
    id: "ln-002",
    moduleId: "mod-jsm-a-pd",
    lineNo: '8"-G-2001',
    service: "Sour Gas to Flare",
    designCode: "ASME B31.3",
    designPressureMpa: 6.2,
    designTempC: 55,
    material: "ASTM A333 Gr.6",
    npsInch: 8,
    schedule: "XS",
    insulated: false,
  },
  {
    id: "ln-003",
    moduleId: "mod-jsm-a-pd",
    lineNo: '4"-WI-3045',
    service: "Water Injection from Wellhead",
    designCode: "ASME B31.3",
    designPressureMpa: 21.0,
    designTempC: 45,
    material: "ASTM A312 TP316L",
    npsInch: 4,
    schedule: "80",
    insulated: false,
  },
  {
    id: "ln-004",
    moduleId: "mod-jsm-a-pd",
    lineNo: '6"-P-1235',
    service: "Crude Oil from V-202",
    designCode: "ASME B31.3",
    designPressureMpa: 4.0,
    designTempC: 65,
    material: "ASTM A106 Gr.B",
    npsInch: 6,
    schedule: "80",
    insulated: true,
    insulationType: "Mineral wool 50 mm",
  },
  {
    id: "ln-005",
    moduleId: "mod-jsm-a-ud",
    lineNo: '3"-GL-4012',
    service: "Gas Lift to Wellbays",
    designCode: "ASME B31.3",
    designPressureMpa: 14.0,
    designTempC: 60,
    material: "ASTM A106 Gr.B",
    npsInch: 3,
    schedule: "80",
    insulated: false,
  },
  {
    id: "ln-006",
    moduleId: "mod-jsm-a-ud",
    lineNo: '2"-CH-5008',
    service: "Methanol Chemical Injection",
    designCode: "ASME B31.3",
    designPressureMpa: 24.0,
    designTempC: 40,
    material: "ASTM A312 TP316L",
    npsInch: 2,
    schedule: "80S",
    insulated: false,
  },
];

// ──────────────────────────────────────────────────────────────────────
// Drawings — SIMULATED
// ──────────────────────────────────────────────────────────────────────
export const drawings: Drawing[] = [
  {
    id: "dwg-001",
    lineId: "ln-001",
    dwgNo: "ISO-JSM-A-PD-1234-001",
    revision: "Rev 2",
    title: "Crude Oil Manifold to FPF-003 FPSO Riser",
    uploadedAt: "2024-03-12",
    cmlCount: 12,
  },
  {
    id: "dwg-002",
    lineId: "ln-001",
    dwgNo: "ISO-JSM-A-PD-1234-002",
    revision: "Rev 1",
    title: "Separator V-201 to Crude Booster Pump",
    uploadedAt: "2024-03-12",
    cmlCount: 8,
  },
  {
    id: "dwg-003",
    lineId: "ln-002",
    dwgNo: "ISO-JSM-A-PD-2001-001",
    revision: "Rev 3",
    title: "Sour Gas Outlet V-201 to Flare KO Drum",
    uploadedAt: "2024-08-20",
    cmlCount: 15,
  },
  {
    id: "dwg-004",
    lineId: "ln-003",
    dwgNo: "ISO-JSM-A-PD-3045-001",
    revision: "Rev 1",
    title: "Water Injection Manifold to Wellhead Trees",
    uploadedAt: "2025-01-05",
    cmlCount: 10,
  },
  {
    id: "dwg-005",
    lineId: "ln-004",
    dwgNo: "ISO-JSM-A-PD-1235-001",
    revision: "Rev 2",
    title: "Crude Oil from V-202 to Export Header",
    uploadedAt: "2024-03-12",
    cmlCount: 11,
  },
  {
    id: "dwg-006",
    lineId: "ln-005",
    dwgNo: "ISO-JSM-A-UD-4012-001",
    revision: "Rev 1",
    title: "Gas Lift Distribution to Wellbays 1-4",
    uploadedAt: "2024-11-08",
    cmlCount: 7,
  },
  {
    id: "dwg-007",
    lineId: "ln-006",
    dwgNo: "ISO-JSM-A-UD-5008-001",
    revision: "Rev 1",
    title: "Methanol Injection to Christmas Trees",
    uploadedAt: "2025-02-14",
    cmlCount: 6,
  },
];

// ──────────────────────────────────────────────────────────────────────
// CMLs for primary drawing — SIMULATED
// Mock data tells a story: 6" CS line with progressive CUI on insulation
// ──────────────────────────────────────────────────────────────────────

function makeMeasurements(
  cmlId: string,
  baseline: number,
  baselineDate: string,
  cr: number,
  inspections: { date: string; inspector: string }[],
): Measurement[] {
  return inspections.map((insp, idx) => {
    const yearsFromBaseline =
      (new Date(insp.date).getTime() - new Date(baselineDate).getTime()) /
      (365.25 * 24 * 3600 * 1000);
    const rep = Math.max(0.5, baseline - cr * yearsFromBaseline + (Math.random() - 0.5) * 0.04);
    const readings = ["12", "3", "6", "9"].map((pos) => ({
      position: pos,
      value: Math.round((rep + Math.random() * 0.12) * 100) / 100,
    }));
    const actualRep = Math.min(...readings.map((r) => r.value));
    return {
      id: `${cmlId}-m${idx + 1}`,
      cmlId,
      date: insp.date,
      inspector: insp.inspector,
      readings,
      representativeMm: actualRep,
      status: "approved",
      gauge: "Olympus 38DL Plus",
      photoCount: 2 + Math.floor(Math.random() * 4),
    };
  });
}

function classifyStatus(
  current: number,
  tMin: number,
  rL: number,
): "ok" | "watch" | "warn" | "crit" {
  if (current <= tMin) return "crit";
  if (rL <= 3 || current < tMin * 1.2) return "warn";
  if (current < tMin * 1.6 || rL < 8) return "watch";
  return "ok";
}

const inspections = [
  { date: "2018-03-15", inspector: "P.Wong (L2)" },
  { date: "2021-04-02", inspector: "S.Chai (L3)" },
  { date: "2024-05-10", inspector: "S.Chai (L3)" },
];

const cmlSpecs: Array<{
  cmlNo: string;
  desc: string;
  type: CML["type"];
  x: number;
  y: number;
  nominal: number;
  tMin: number;
  baseline: number;
  cr: number;
  cui: boolean;
  insulated: boolean;
  deadLeg: boolean;
}> = [
  { cmlNo: "CML-001", desc: "Elbow E-1 90° 12 o'clock", type: "elbow", x: 0.16, y: 0.62, nominal: 10.97, tMin: 5.42, baseline: 10.85, cr: 0.135, cui: true, insulated: true, deadLeg: false },
  { cmlNo: "CML-002", desc: "Tee T-1 branch 6 o'clock", type: "tee", x: 0.30, y: 0.55, nominal: 10.97, tMin: 5.42, baseline: 10.80, cr: 0.090, cui: true, insulated: true, deadLeg: false },
  { cmlNo: "CML-003", desc: "Straight pipe upstream V-201", type: "general", x: 0.43, y: 0.50, nominal: 10.97, tMin: 5.42, baseline: 10.78, cr: 0.075, cui: true, insulated: true, deadLeg: false },
  { cmlNo: "CML-004", desc: "Reducer 6×4 R-1", type: "reducer", x: 0.53, y: 0.46, nominal: 10.97, tMin: 5.42, baseline: 10.92, cr: 0.110, cui: true, insulated: true, deadLeg: false },
  { cmlNo: "CML-005", desc: "Elbow E-2 90° 6 o'clock CUI risk", type: "elbow", x: 0.62, y: 0.40, nominal: 10.97, tMin: 5.42, baseline: 10.65, cr: 0.36, cui: true, insulated: true, deadLeg: false },
  { cmlNo: "CML-006", desc: "Branch dead leg DL-1", type: "dead_leg", x: 0.68, y: 0.34, nominal: 10.97, tMin: 5.42, baseline: 10.70, cr: 0.42, cui: true, insulated: true, deadLeg: true },
  { cmlNo: "CML-007", desc: "Straight pipe to V-201 inlet", type: "general", x: 0.74, y: 0.40, nominal: 10.97, tMin: 5.42, baseline: 10.88, cr: 0.085, cui: true, insulated: true, deadLeg: false },
  { cmlNo: "CML-008", desc: "Flange F-1 ANSI 300# weld neck", type: "general", x: 0.82, y: 0.40, nominal: 10.97, tMin: 5.42, baseline: 10.92, cr: 0.060, cui: false, insulated: true, deadLeg: false },
  { cmlNo: "CML-009", desc: "Elbow E-3 horizontal turn", type: "elbow", x: 0.30, y: 0.72, nominal: 10.97, tMin: 5.42, baseline: 10.86, cr: 0.155, cui: true, insulated: true, deadLeg: false },
  { cmlNo: "CML-010", desc: "Straight pipe span 2.4m", type: "general", x: 0.43, y: 0.76, nominal: 10.97, tMin: 5.42, baseline: 10.84, cr: 0.080, cui: true, insulated: true, deadLeg: false },
  { cmlNo: "CML-011", desc: "Injection point IP-1 chemical", type: "injection", x: 0.56, y: 0.76, nominal: 10.97, tMin: 5.42, baseline: 10.85, cr: 0.28, cui: false, insulated: true, deadLeg: false },
  { cmlNo: "CML-012", desc: "Elbow E-4 splash zone CUI", type: "elbow", x: 0.69, y: 0.78, nominal: 10.97, tMin: 5.42, baseline: 10.60, cr: 0.51, cui: true, insulated: true, deadLeg: false },
];

// Additional CMLs for the rest of the JSM-A drawing set (dwg-002 to dwg-007).
// Each spec is identified by `key` (used in the IsoForge model anchors).
type ExtraSpec = Omit<typeof cmlSpecs[number], "x" | "y"> & { key: string };
const extraSpecsByDrawing: Record<string, ExtraSpec[]> = {
  // dwg-002 — Separator V-201 to Crude Booster Pump (8 CMLs)
  "dwg-002": [
    { key: "d2-1", cmlNo: "CML-101", desc: "V-201 outlet nozzle weld",      type: "general", nominal: 10.97, tMin: 5.42, baseline: 10.90, cr: 0.105, cui: false, insulated: true,  deadLeg: false },
    { key: "d2-2", cmlNo: "CML-102", desc: "Elbow E-21 vertical drop",       type: "elbow",   nominal: 10.97, tMin: 5.42, baseline: 10.74, cr: 0.140, cui: false, insulated: true,  deadLeg: false },
    { key: "d2-3", cmlNo: "CML-103", desc: "Strainer ST-201 inlet",          type: "general", nominal: 10.97, tMin: 5.42, baseline: 10.83, cr: 0.090, cui: false, insulated: false, deadLeg: false },
    { key: "d2-4", cmlNo: "CML-104", desc: "Pump P-101A suction flange",     type: "general", nominal: 10.97, tMin: 5.42, baseline: 10.92, cr: 0.050, cui: false, insulated: false, deadLeg: false },
    { key: "d2-5", cmlNo: "CML-105", desc: "Tee T-21 branch to P-101B",      type: "tee",     nominal: 10.97, tMin: 5.42, baseline: 10.78, cr: 0.115, cui: false, insulated: false, deadLeg: false },
    { key: "d2-6", cmlNo: "CML-106", desc: "Discharge check CV-101",         type: "general", nominal: 10.97, tMin: 5.42, baseline: 10.86, cr: 0.070, cui: false, insulated: false, deadLeg: false },
  ],
  // dwg-003 — Sour Gas Outlet V-201 to Flare KO Drum V-301 (15 CMLs — H2S, CUI)
  "dwg-003": [
    { key: "d3-1", cmlNo: "CML-201", desc: "V-201 gas outlet elbow",         type: "elbow",   nominal: 12.70, tMin: 6.35, baseline: 12.55, cr: 0.18,  cui: true,  insulated: true, deadLeg: false },
    { key: "d3-2", cmlNo: "CML-202", desc: "Demister DM-201 outlet",         type: "general", nominal: 12.70, tMin: 6.35, baseline: 12.48, cr: 0.21,  cui: true,  insulated: true, deadLeg: false },
    { key: "d3-3", cmlNo: "CML-203", desc: "Horizontal run support S-31",    type: "general", nominal: 12.70, tMin: 6.35, baseline: 12.40, cr: 0.30,  cui: true,  insulated: true, deadLeg: false },
    { key: "d3-4", cmlNo: "CML-204", desc: "PSV-201 inlet tee",              type: "tee",     nominal: 12.70, tMin: 6.35, baseline: 12.32, cr: 0.39,  cui: true,  insulated: true, deadLeg: true  },
    { key: "d3-5", cmlNo: "CML-205", desc: "Elbow before flare KO V-301",    type: "elbow",   nominal: 12.70, tMin: 6.35, baseline: 12.10, cr: 0.45,  cui: true,  insulated: true, deadLeg: false },
    { key: "d3-6", cmlNo: "CML-206", desc: "Splash-zone elbow at riser",     type: "elbow",   nominal: 12.70, tMin: 6.35, baseline: 11.85, cr: 0.55,  cui: true,  insulated: true, deadLeg: false },
    { key: "d3-7", cmlNo: "CML-207", desc: "V-301 inlet flange",             type: "general", nominal: 12.70, tMin: 6.35, baseline: 12.62, cr: 0.08,  cui: false, insulated: true, deadLeg: false },
  ],
  // dwg-004 — Water Injection Manifold (10 CMLs — high pressure 21 MPa)
  "dwg-004": [
    { key: "d4-1", cmlNo: "CML-301", desc: "Pump discharge flange",          type: "general", nominal: 8.56,  tMin: 3.50, baseline: 8.50, cr: 0.040, cui: false, insulated: false, deadLeg: false },
    { key: "d4-2", cmlNo: "CML-302", desc: "Header tee T-301",               type: "tee",     nominal: 8.56,  tMin: 3.50, baseline: 8.48, cr: 0.050, cui: false, insulated: false, deadLeg: false },
    { key: "d4-3", cmlNo: "CML-303", desc: "Elbow E-31 manifold",            type: "elbow",   nominal: 8.56,  tMin: 3.50, baseline: 8.42, cr: 0.075, cui: false, insulated: false, deadLeg: false },
    { key: "d4-4", cmlNo: "CML-304", desc: "Choke valve CV-301 inlet",       type: "general", nominal: 8.56,  tMin: 3.50, baseline: 8.46, cr: 0.060, cui: false, insulated: false, deadLeg: false },
    { key: "d4-5", cmlNo: "CML-305", desc: "Branch to wellhead WH-01",       type: "tee",     nominal: 8.56,  tMin: 3.50, baseline: 8.40, cr: 0.085, cui: false, insulated: false, deadLeg: false },
    { key: "d4-6", cmlNo: "CML-306", desc: "Christmas tree connection",      type: "general", nominal: 8.56,  tMin: 3.50, baseline: 8.43, cr: 0.070, cui: false, insulated: false, deadLeg: false },
  ],
  // dwg-005 — Crude Oil from V-202 to Export Header (11 CMLs)
  "dwg-005": [
    { key: "d5-1", cmlNo: "CML-401", desc: "V-202 oil outlet elbow",         type: "elbow",   nominal: 10.97, tMin: 5.42, baseline: 10.88, cr: 0.110, cui: true,  insulated: true, deadLeg: false },
    { key: "d5-2", cmlNo: "CML-402", desc: "Heater H-301 inlet",             type: "general", nominal: 10.97, tMin: 5.42, baseline: 10.82, cr: 0.140, cui: true,  insulated: true, deadLeg: false },
    { key: "d5-3", cmlNo: "CML-403", desc: "Heater H-301 outlet",            type: "general", nominal: 10.97, tMin: 5.42, baseline: 10.76, cr: 0.160, cui: true,  insulated: true, deadLeg: false },
    { key: "d5-4", cmlNo: "CML-404", desc: "FV-201 control valve outlet",    type: "general", nominal: 10.97, tMin: 5.42, baseline: 10.84, cr: 0.105, cui: false, insulated: true, deadLeg: false },
    { key: "d5-5", cmlNo: "CML-405", desc: "Export header tie-in",           type: "tee",     nominal: 10.97, tMin: 5.42, baseline: 10.70, cr: 0.220, cui: true,  insulated: true, deadLeg: false },
    { key: "d5-6", cmlNo: "CML-406", desc: "Sample takeoff dead leg",        type: "dead_leg",nominal: 10.97, tMin: 5.42, baseline: 10.55, cr: 0.38,  cui: false, insulated: true, deadLeg: true  },
  ],
  // dwg-006 — Gas Lift Distribution (3" header, branches to W-01..W-04)
  "dwg-006": [
    { key: "d6-1", cmlNo: "CML-501", desc: "Gas-lift header inlet",          type: "general", nominal: 5.49,  tMin: 2.20, baseline: 5.42, cr: 0.030, cui: false, insulated: false, deadLeg: false },
    { key: "d6-2", cmlNo: "CML-502", desc: "Tee T-50 to W-01",               type: "tee",     nominal: 5.49,  tMin: 2.20, baseline: 5.36, cr: 0.045, cui: false, insulated: false, deadLeg: false },
    { key: "d6-3", cmlNo: "CML-503", desc: "Tee T-51 to W-02",               type: "tee",     nominal: 5.49,  tMin: 2.20, baseline: 5.38, cr: 0.040, cui: false, insulated: false, deadLeg: false },
    { key: "d6-4", cmlNo: "CML-504", desc: "Tee T-52 to W-03",               type: "tee",     nominal: 5.49,  tMin: 2.20, baseline: 5.34, cr: 0.052, cui: false, insulated: false, deadLeg: false },
    { key: "d6-5", cmlNo: "CML-505", desc: "Injection point at tree W-03",   type: "injection",nominal:5.49,  tMin: 2.20, baseline: 5.25, cr: 0.095, cui: false, insulated: false, deadLeg: false },
    { key: "d6-6", cmlNo: "CML-506", desc: "Elbow E-50 header turn to W-04", type: "elbow",   nominal: 5.49,  tMin: 2.20, baseline: 5.30, cr: 0.058, cui: false, insulated: false, deadLeg: false },
    { key: "d6-7", cmlNo: "CML-507", desc: "Injection point at tree W-04",   type: "injection",nominal:5.49,  tMin: 2.20, baseline: 5.22, cr: 0.105, cui: false, insulated: false, deadLeg: false },
  ],
  // dwg-007 — Methanol Injection (2" stainless, distributes to W-01..W-04)
  "dwg-007": [
    { key: "d7-1", cmlNo: "CML-601", desc: "Methanol tank outlet",           type: "general", nominal: 3.91,  tMin: 1.65, baseline: 3.88, cr: 0.010, cui: false, insulated: false, deadLeg: false },
    { key: "d7-2", cmlNo: "CML-602", desc: "Metering pump discharge",        type: "general", nominal: 3.91,  tMin: 1.65, baseline: 3.86, cr: 0.012, cui: false, insulated: false, deadLeg: false },
    { key: "d7-3", cmlNo: "CML-603", desc: "Injection point IP-1 W-01",      type: "injection",nominal:3.91,  tMin: 1.65, baseline: 3.78, cr: 0.022, cui: false, insulated: false, deadLeg: false },
    { key: "d7-4", cmlNo: "CML-604", desc: "Injection point IP-2 W-02",      type: "injection",nominal:3.91,  tMin: 1.65, baseline: 3.80, cr: 0.018, cui: false, insulated: false, deadLeg: false },
    { key: "d7-5", cmlNo: "CML-605", desc: "Injection point IP-3 W-03",      type: "injection",nominal:3.91,  tMin: 1.65, baseline: 3.76, cr: 0.025, cui: false, insulated: false, deadLeg: false },
    { key: "d7-6", cmlNo: "CML-606", desc: "Injection point IP-4 W-04",      type: "injection",nominal:3.91,  tMin: 1.65, baseline: 3.74, cr: 0.028, cui: false, insulated: false, deadLeg: false },
  ],
};

function buildCml(
  id: string,
  drawingId: string,
  s: { cmlNo: string; desc: string; type: CML["type"]; nominal: number; tMin: number; baseline: number; cr: number; cui: boolean; insulated: boolean; deadLeg: boolean },
  pdfX = 0.5,
  pdfY = 0.5,
  parent: CmlParent | null = null,
): CML {
  const measurements = makeMeasurements(id, s.baseline, "2018-03-15", s.cr, inspections);
  const latest = measurements[measurements.length - 1];
  const prior = measurements[measurements.length - 2];
  const baseline = measurements[0];

  const yearsBaseline = 6.15;
  const yearsST = 3.10;
  const ltCR = (baseline.representativeMm - latest.representativeMm) / yearsBaseline;
  const stCR = (prior.representativeMm - latest.representativeMm) / yearsST;
  const effectiveCR = Math.max(stCR, ltCR, 0.001);
  const rL = Math.max(0, (latest.representativeMm - s.tMin) / effectiveCR);

  let intervalYr = Math.min(rL / 2, s.cui ? 5 : 10);
  intervalYr = Math.min(intervalYr, 10);
  const nextDate = new Date(latest.date);
  nextDate.setDate(nextDate.getDate() + Math.round(intervalYr * 365.25));

  const status = classifyStatus(latest.representativeMm, s.tMin, rL);

  return {
    id,
    drawingId,
    parent: parent ?? { kind: "piping", drawingId },
    cmlNo: s.cmlNo,
    description: s.desc,
    type: s.type,
    pdfPosition: { page: 1, x: pdfX, y: pdfY },
    nominalMm: s.nominal,
    minimumMm: s.tMin,
    retirementMm: s.tMin * 1.1,
    baselineMm: s.baseline,
    baselineDate: "2018-03-15",
    isCuiRisk: s.cui,
    isInsulated: s.insulated,
    isDeadLeg: s.deadLeg,
    measurements,
    calc: {
      shortTermCrMmYr: Math.max(stCR, 0),
      longTermCrMmYr: Math.max(ltCR, 0),
      effectiveCrMmYr: effectiveCR,
      remainingLifeYr: Math.min(rL, 99.9),
      nextInspectionDate: nextDate.toISOString().slice(0, 10),
      status,
    },
  };
}

const originalCmls: CML[] = cmlSpecs.map((s) =>
  buildCml(`cml-${s.cmlNo.toLowerCase().replace("-", "")}`, "dwg-001", s, s.x, s.y),
);

const extraCmls: CML[] = Object.entries(extraSpecsByDrawing).flatMap(([drawingId, specs]) =>
  specs.map((s) => buildCml(`cml-${drawingId}-${s.key}`, drawingId, s)),
);

// ──────────────────────────────────────────────────────────────────────
// Equipment CMLs — vessels + tanks (API 510 / 653)
// ──────────────────────────────────────────────────────────────────────
import {
  vessels as vesselDefs,
  tanks as tankDefs,
  vesselCmlPlan,
  tankCmlPlan,
  makeEquipmentCml,
} from "./equipmentData";

const vesselCmls: CML[] = vesselDefs.flatMap((v) =>
  vesselCmlPlan(v).map((entry, i) =>
    makeEquipmentCml(
      `cml-${v.id}-${(i + 1).toString().padStart(3, "0")}`,
      { kind: "vessel", vesselId: v.id, region: entry.region },
      entry.seed,
      "2018-03-15",
      inspections,
      makeMeasurements,
      classifyStatus,
    ),
  ),
);

const tankCmls: CML[] = tankDefs.flatMap((t) =>
  tankCmlPlan(t).map((entry, i) =>
    makeEquipmentCml(
      `cml-${t.id}-${(i + 1).toString().padStart(3, "0")}`,
      { kind: "tank", tankId: t.id, region: entry.region },
      entry.seed,
      "2018-03-15",
      inspections,
      makeMeasurements,
      classifyStatus,
    ),
  ),
);

export const cmls: CML[] = [...originalCmls, ...extraCmls, ...vesselCmls, ...tankCmls];
export const vessels = vesselDefs;
export const tanks = tankDefs;

// ──────────────────────────────────────────────────────────────────────
// P&ID documents per module (only Production Deck has one in this mockup)
// ──────────────────────────────────────────────────────────────────────
export const pids: PIDDoc[] = [
  {
    id: "pid-jsm-a-pd-001",
    moduleId: "mod-jsm-a-pd",
    no: "PID-JSM-A-PD-001",
    revision: "Rev 4",
    title: "Crude Oil Production Train",
  },
];

export function getModulePids(moduleId: string): PIDDoc[] {
  return pids.filter((p) => p.moduleId === moduleId);
}

export function getVessel(id: string): Vessel | undefined {
  return vesselDefs.find((v) => v.id === id);
}
export function getTank(id: string): Tank | undefined {
  return tankDefs.find((t) => t.id === id);
}
export function getVesselCmls(vesselId: string): CML[] {
  return cmls.filter((c) => c.parent.kind === "vessel" && c.parent.vesselId === vesselId);
}
export function getTankCmls(tankId: string): CML[] {
  return cmls.filter((c) => c.parent.kind === "tank" && c.parent.tankId === tankId);
}
export function getPlatformVessels(platformId: string): Vessel[] {
  return vesselDefs.filter((v) => v.platformId === platformId);
}
export function getPlatformTanks(platformId: string): Tank[] {
  return tankDefs.filter((t) => t.platformId === platformId);
}

// ──────────────────────────────────────────────────────────────────────
// Inspection Campaigns — SIMULATED
// ──────────────────────────────────────────────────────────────────────
export const inspectionCampaigns: InspectionCampaign[] = [
  {
    id: "ins-2024-005",
    no: "INS-2024-005",
    inspector: "S.Chai (L3)",
    startDate: "2024-05-08",
    endDate: "2024-05-14",
    cmlCount: 142,
    status: "complete",
  },
  {
    id: "ins-2026-001",
    no: "INS-2026-001",
    inspector: "P.Wong (L2)",
    startDate: "2026-02-04",
    endDate: "2026-02-09",
    cmlCount: 86,
    status: "pending_review",
  },
  {
    id: "ins-2026-002",
    no: "INS-2026-002",
    inspector: "N.Sukhum (L2)",
    startDate: "2026-04-22",
    endDate: "2026-04-25",
    cmlCount: 38,
    status: "in_progress",
  },
];

// ──────────────────────────────────────────────────────────────────────
// Reports — SIMULATED
// ──────────────────────────────────────────────────────────────────────
export const reports: Report[] = [
  {
    id: "rep-001",
    no: "VLE-INS-2026-PR-0021",
    title: "Q1 2026 Anomaly Report — Jasmine A",
    type: "anomaly",
    scope: "JSM-A · 2026-01-01 to 2026-03-31",
    generatedBy: "S.Chai (L3)",
    generatedAt: "2026-04-02 14:22",
    status: "final",
    cmlCount: 18,
    criticalCount: 4,
  },
  {
    id: "rep-002",
    no: "VLE-INS-2026-CR-0019",
    title: "Inspection Campaign INS-2026-001",
    type: "inspection_campaign",
    scope: "JSM-A Production Deck",
    generatedBy: "P.Wong (L2)",
    generatedAt: "2026-02-14 09:48",
    status: "approved",
    cmlCount: 86,
    criticalCount: 2,
  },
  {
    id: "rep-003",
    no: "VLE-INS-2026-DR-0014",
    title: "Drawing Summary — ISO-JSM-A-PD-1234-001 Rev 2",
    type: "drawing_summary",
    scope: 'Line 6"-P-1234',
    generatedBy: "S.Chai (L3)",
    generatedAt: "2026-01-28 16:05",
    status: "approved",
    cmlCount: 12,
    criticalCount: 1,
  },
  {
    id: "rep-004",
    no: "VLE-INS-2025-DMF-0001",
    title: "DMF Annual Integrity Submission FY2025",
    type: "dmf_annual",
    scope: "All Sites · 2025-01-01 to 2025-12-31",
    generatedBy: "K.Sakpolachet (L3)",
    generatedAt: "2026-01-15 11:30",
    status: "final",
    cmlCount: 2560,
    criticalCount: 12,
  },
  {
    id: "rep-005",
    no: "VLE-INS-2025-PS-0042",
    title: "Platform Summary — Manora A",
    type: "platform_summary",
    scope: "MNR-A",
    generatedBy: "S.Chai (L3)",
    generatedAt: "2025-12-30 13:14",
    status: "final",
    cmlCount: 318,
    criticalCount: 1,
  },
];

// ──────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────
export function getDrawingCmls(drawingId: string): CML[] {
  return cmls.filter((c) => c.drawingId === drawingId);
}

export function getCml(cmlId: string): CML | undefined {
  return cmls.find((c) => c.id === cmlId);
}

export function getLineForDrawing(drawingId: string): Line | undefined {
  const dwg = drawings.find((d) => d.id === drawingId);
  if (!dwg) return undefined;
  return lines.find((l) => l.id === dwg.lineId);
}

export function getPlatformLines(platformId: string): Line[] {
  const mods = modules.filter((m) => m.platformId === platformId).map((m) => m.id);
  return lines.filter((l) => mods.includes(l.moduleId));
}

export function getSitePlatforms(siteId: string): Platform[] {
  return platforms.filter((p) => p.siteId === siteId);
}

export function getSite(siteId: string): Site | undefined {
  return sites.find((s) => s.id === siteId);
}

export function summarizeDrawingStatus(drawingId: string): {
  total: number;
  ok: number;
  watch: number;
  warn: number;
  crit: number;
} {
  const list = getDrawingCmls(drawingId);
  return {
    total: list.length,
    ok: list.filter((c) => c.calc.status === "ok").length,
    watch: list.filter((c) => c.calc.status === "watch").length,
    warn: list.filter((c) => c.calc.status === "warn").length,
    crit: list.filter((c) => c.calc.status === "crit").length,
  };
}

/** Reduce a CML list to its worst status — used by AssetTree status dots. */
export function worstStatusOf(cmls: CML[]): CMLStatus | null {
  if (cmls.length === 0) return null;
  const order: CMLStatus[] = ["ok", "watch", "warn", "crit"];
  let worst: CMLStatus = "ok";
  for (const c of cmls) {
    if (order.indexOf(c.calc.status) > order.indexOf(worst)) worst = c.calc.status;
  }
  return worst;
}

export function getDrawingWorstStatus(drawingId: string): CMLStatus | null {
  return worstStatusOf(getDrawingCmls(drawingId));
}
export function getVesselWorstStatus(vesselId: string): CMLStatus | null {
  return worstStatusOf(getVesselCmls(vesselId));
}
export function getTankWorstStatus(tankId: string): CMLStatus | null {
  return worstStatusOf(getTankCmls(tankId));
}
export function getLineWorstStatus(lineId: string): CMLStatus | null {
  const drawingIds = drawings.filter((d) => d.lineId === lineId).map((d) => d.id);
  const lineCmls = cmls.filter((c) => drawingIds.includes(c.drawingId));
  return worstStatusOf(lineCmls);
}
